import {FC, useCallback, useState} from 'react';

import {FullGroup} from '@shared/models/api_models';

import {Input} from '@shared_web/components/core/input';
import {hideModal} from '@shared_web/components/core/modal';
import {showAlert, showSuccess} from '@shared_web/components/core/notifications';
import {notifyError} from '@shared_web/lib/notification';

import {GenericModalForm} from '@mavismart_frontend/components/generic_modal_form';
import {translate} from '@mavismart_frontend/components/translate';
import {apiCall} from '@mavismart_frontend/lib/api';
import {INPUT_THEME, INPUT_THEME_ERROR} from '@mavismart_frontend/lib/constants';
import {getUserLanguage, useAccount} from '@mavismart_frontend/stores/account_store';
import {renameGroup} from '@mavismart_frontend/stores/groups_store';

interface UpdateGroupNameFormProps {
  group?: FullGroup;
}

export const UpdateGroupNameForm: FC<UpdateGroupNameFormProps> = ({group}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [updatedName, setUpdatedName] = useState<string | undefined>(undefined);
  const account = useAccount();
  const [hasError, setHasError] = useState(false);

  const handleConfirmUpdateGroupName = useCallback(async () => {
    if (group === undefined) {
      return;
    }
    if (!updatedName) {
      setHasError(true);
      showAlert(translate('all_fields_required', getUserLanguage(account)));
      return;
    }
    setHasError(false);
    try {
      setIsLoading(true);
      await apiCall('POST /update-group-name', {groupId: group.group.id, newName: updatedName});
      renameGroup(group.group.id, updatedName);
      showSuccess(translate('group_name_updated', getUserLanguage(account)));
    } catch (error) {
      notifyError(error);
      setHasError(true);
    } finally {
      setIsLoading(false);
      hideModal();
      setUpdatedName(undefined);
    }
  }, [updatedName, group, account]);

  return (
    <GenericModalForm
      onSubmit={handleConfirmUpdateGroupName}
      loading={isLoading}
      childrenInput={
        <Input
          width={309}
          type="text"
          value={updatedName}
          syncState={setUpdatedName}
          placeholder={translate('group_name_placeholder', getUserLanguage(account))}
          themeOverrides={
            hasError
              ? {
                  input: {
                    ...INPUT_THEME_ERROR,
                  },
                }
              : {input: {...INPUT_THEME}}
          }
        />
      }
    />
  );
};

UpdateGroupNameForm.displayName = 'UpdateGroupNameForm';
